import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, Copy, PencilSimple } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;
const PROGRAMS = ['','gravida','pos_parto','introducao_alimentar','emagrecimento','pre_concecao','geral'];
const PROG_LABELS = { gravida:'Gravidez', pos_parto:'Pós-parto', introducao_alimentar:'Introdução alimentar', emagrecimento:'Emagrecimento', pre_concecao:'Pré-conceção', geral:'Genérico' };

export default function SuplementacaoTemplatesPage() {
  useRequireAdmin();
  const confirmDelete = useConfirmDelete();
  const [templates, setTemplates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', program: '', supplements: [{ name: '', dose: '', posology: '', timing: '', duration: '', notes: '' }] });

  const fetch = useCallback(async () => { try { const { data } = await axios.get(`${API}/api/admin/supp-templates`); setTemplates(data); } catch {} }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const addSupp = () => setForm({ ...form, supplements: [...form.supplements, { name: '', dose: '', posology: '', timing: '', duration: '', notes: '' }] });
  const updateSupp = (i, f, v) => { const s = [...form.supplements]; s[i] = { ...s[i], [f]: v }; setForm({ ...form, supplements: s }); };
  const removeSupp = (i) => setForm({ ...form, supplements: form.supplements.filter((_, idx) => idx !== i) });

  const save = async () => {
    try {
      const payload = { ...form, supplements: form.supplements.filter(s => s.name.trim()) };
      if (editId) { await axios.put(`${API}/api/admin/supp-templates/${editId}`, payload); }
      else { await axios.post(`${API}/api/admin/supp-templates`, payload); }
      toast.success('Template guardado'); setShowForm(false); setEditId(null); fetch();
    } catch { toast.error('Erro'); }
  };
  const del = async (id) => {
    const t = templates.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o template de suplementação "${t?.name || ''}".` });
    if (!ok) return;
    await axios.delete(`${API}/api/admin/supp-templates/${id}`); fetch();
  };
  const dup = async (id) => { await axios.post(`${API}/api/admin/supp-templates/${id}/duplicate`); toast.success('Duplicado'); fetch(); };

  return (
    <div className="fade-in" data-testid="supp-templates-page">
      <div className="flex items-center justify-between mb-5">
        <div><h2 className="font-heading font-medium text-xl text-brand-text">Templates de Suplementação</h2><p className="text-xs text-brand-muted mt-0.5">Protocolos de suplementação reutilizáveis</p></div>
        <button onClick={() => { setEditId(null); setForm({ name: '', program: '', supplements: [{ name: '', dose: '', posology: '', timing: '', duration: '', notes: '' }] }); setShowForm(true); }} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2"><Plus size={16} /> Novo template</button>
      </div>
      {templates.length === 0 ? <p className="text-center text-brand-muted py-8">Sem templates.</p> : (
        <div className="space-y-2">
          {templates.map(t => (
            <div key={t.id} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`supp-tmpl-${t.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-medium text-brand-text">{t.name}</h3>
                  <div className="flex gap-2 mt-0.5">{t.program && <span className="text-[9px] bg-brand-bg text-brand-muted px-2 py-0.5 rounded-full">{PROG_LABELS[t.program] || t.program}</span>}<span className="text-[9px] text-brand-muted">{(t.supplements || []).length} suplementos</span></div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setEditId(t.id); setForm({ name: t.name, program: t.program || '', supplements: t.supplements?.length ? t.supplements : [{ name: '', dose: '', posology: '', timing: '', duration: '', notes: '' }] }); setShowForm(true); }} className="p-1 text-brand-placeholder hover:text-brand-green"><PencilSimple size={14} /></button>
                  <button onClick={() => dup(t.id)} className="p-1 text-brand-placeholder hover:text-brand-green"><Copy size={14} /></button>
                  <button onClick={() => del(t.id)} className="p-1 text-brand-placeholder hover:text-red-500"><Trash size={14} /></button>
                </div>
              </div>
              <div className="mt-2 space-y-1">{(t.supplements || []).map((s, i) => <p key={i} className="text-xs text-brand-muted">{s.name} {s.dose}{s.timing ? `, ${s.timing}` : ''}</p>)}</div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editId ? 'Editar template' : 'Novo template'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-brand-muted mb-1 block">Nome</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-xl border-brand-border" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Programa</Label><select value={form.program} onChange={e => setForm({ ...form, program: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white"><option value="">Todos</option>{PROGRAMS.filter(Boolean).map(p => <option key={p} value={p}>{PROG_LABELS[p] || p}</option>)}</select></div>
            </div>
            <Label className="text-xs text-brand-muted">Suplementos</Label>
            {form.supplements.map((s, i) => (
              <div key={i} className="bg-brand-bg/30 rounded-xl p-2 space-y-1">
                <div className="grid grid-cols-3 gap-1">
                  <Input value={s.name} onChange={e => updateSupp(i, 'name', e.target.value)} placeholder="Suplemento" className="rounded-lg border-brand-border text-xs h-8" />
                  <Input value={s.dose} onChange={e => updateSupp(i, 'dose', e.target.value)} placeholder="Dose" className="rounded-lg border-brand-border text-xs h-8" />
                  <Input value={s.posology} onChange={e => updateSupp(i, 'posology', e.target.value)} placeholder="Posologia" className="rounded-lg border-brand-border text-xs h-8" />
                </div>
                <div className="grid grid-cols-3 gap-1">
                  <Input value={s.timing} onChange={e => updateSupp(i, 'timing', e.target.value)} placeholder="Momento" className="rounded-lg border-brand-border text-xs h-8" />
                  <Input value={s.duration} onChange={e => updateSupp(i, 'duration', e.target.value)} placeholder="Duração" className="rounded-lg border-brand-border text-xs h-8" />
                  <div className="flex items-center gap-1"><Input value={s.notes} onChange={e => updateSupp(i, 'notes', e.target.value)} placeholder="Obs." className="rounded-lg border-brand-border text-xs h-8 flex-1" />{form.supplements.length > 1 && <button onClick={() => removeSupp(i)} className="text-red-400"><Trash size={12} /></button>}</div>
                </div>
              </div>
            ))}
            <button onClick={addSupp} className="text-xs text-brand-green flex items-center gap-1"><Plus size={12} /> Adicionar suplemento</button>
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
